import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authLogger } from '@/core/utils/logger';
import { setUserIdMapping } from '../../auth/services/userIdentity';
import { getStorageItem, setStorageItem, removeStorageItem, STORAGE_KEYS } from '@/core/utils/local-storage';

// SESSION/UI-FOCUSED: User profile interface for session management
export interface UserProfileSession {
  // SESSION CACHE: Basic identifying info for app-wide access
  id: string | null;
  stytchUserId?: string | null;
  organizationId?: string | null;

  // ESSENTIAL UI DATA: Required for avatar and display components
  email?: string;
  profileImageUrl?: string | null;
  avatarColor?: string | null;

  // COMPUTED UI FIELDS: Enhanced data for UI components
  fullName: string;
  initials: string;
  avatar: {
    type: 'image' | 'text';
    value: string;
    initials: string;
  };

  // SESSION STATE: Authentication and permissions
  role?: string;
  isOnline?: boolean;
}

interface UserProfileUiState {
  // SESSION CACHE: Enhanced user data for app-wide access
  session: UserProfileSession | null;
  
  // UI STATE: Loading and error status
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  
  // UI STATE: Last sync timestamp for debugging
  lastSyncAt: number | null;
}

// Helper function to get persisted session from localStorage
const getPersistedSession = (): UserProfileSession | null => {
  try {
    const session = getStorageItem(STORAGE_KEYS.USER_PROFILE_SESSION) as UserProfileSession | null;
    if (session) {
      // Validate that essential fields exist
      if (session.id && session.email) {
        return session;
      }
    }
  } catch (e) {
    authLogger.error('[userProfileSlice] Failed to load persisted session:', e);
    removeStorageItem(STORAGE_KEYS.USER_PROFILE_SESSION);
  }
  return null;
};

const initialState: UserProfileUiState = {
  session: getPersistedSession(),
  status: getPersistedSession() ? 'succeeded' : 'idle',
  error: null,
  lastSyncAt: null,
};

// HELPER: Generate computed UI fields
const generateComputedFields = (userData: { name?: { firstName?: string; lastName?: string }; firstName?: string; lastName?: string; profileImageUrl?: string | null; avatarColor?: string | null }): Pick<UserProfileSession, 'fullName' | 'initials' | 'avatar'> => {
  const firstName = userData?.name?.firstName || userData?.firstName || '';
  const lastName = userData?.name?.lastName || userData?.lastName || '';

  const fullName = `${firstName} ${lastName}`.trim() || 'User';
  const initials = (firstName[0] || '') + (lastName[0] || '') || 'U';

  // Properly handle profile image URL
  const profileImageUrl = userData?.profileImageUrl;
  const hasProfileImage = profileImageUrl && profileImageUrl !== '' && profileImageUrl !== null;

  const avatar = {
    type: (hasProfileImage ? 'image' : 'text') as 'image' | 'text',
    value: hasProfileImage ? profileImageUrl : (userData?.avatarColor || '#3B82F6'),
    initials: initials.toUpperCase()
  };

  return {
    fullName,
    initials: initials.toUpperCase(),
    avatar
  };
};

const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    // SESSION MANAGEMENT: Set session cache from any user data source
    setUserSession(state, action: PayloadAction<{ id?: string | null; apiId?: string; stytchUserId?: string | null; user_id?: string; email?: string; firstName?: string; lastName?: string; name?: { firstName?: string; lastName?: string }; profileImageUrl?: string | null; avatarColor?: string | null; organizationId?: string | null; phoneNumber?: string | null; role?: string | { name?: string }; isOnline?: boolean; isActive?: boolean; setupCompleted?: boolean; createdAt?: string | null; updatedAt?: string | null } | null>) {
      if (action.payload === null) {
        state.session = null;
        state.status = 'idle';
        state.error = null;
        // Clear persisted session on logout
        removeStorageItem(STORAGE_KEYS.USER_PROFILE_SESSION);
      } else {
        const userData = action.payload;
        const computedFields = generateComputedFields(userData);

        const apiId = userData.id || userData.apiId || null;
        const authId = userData.stytchUserId || userData.user_id || apiId;

        // Ensure user ID mapping is always in sync with session
        if (authId && apiId) {
          try {
            setUserIdMapping(authId, apiId);
          } catch (e) {
            authLogger.error('[userProfileSlice] Failed to set user ID mapping:', e);
          }
        }

        const newSession: UserProfileSession = {
          id: apiId,
          stytchUserId: authId,
          organizationId: userData.organizationId || null,
          // FIXED: Handle both string role (from backend) and object role (from other sources)
          // Backend returns role as a string ('admin' or 'agent'), not an object
          role: typeof userData.role === 'string' ? userData.role : (userData.role?.name || undefined),
          isOnline: userData.isOnline ?? true,
          // ESSENTIAL UI DATA: Include for component compatibility
          email: userData.email || undefined,
          profileImageUrl: userData.profileImageUrl || null,
          avatarColor: userData.avatarColor || null,
          ...computedFields
        };

        state.session = newSession;
        state.status = 'succeeded';
        state.lastSyncAt = Date.now();

        // Persist session to localStorage for instant hydration on reload
        try {
          setStorageItem(STORAGE_KEYS.USER_PROFILE_SESSION, newSession);
        } catch (e) {
          authLogger.error('[userProfileSlice] Failed to persist session:', e);
        }
      }
      state.error = null;
    },
    
    // UI STATUS: Update loading/error states
    setProfileStatus(state, action: PayloadAction<'idle' | 'loading' | 'succeeded' | 'failed'>) {
      state.status = action.payload;
    },
    
    setProfileError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      if (action.payload) {
        state.status = 'failed';
      }
    },
    
    // SESSION UPDATE: Update specific session fields (for real-time updates)
    updateUserSession(state, action: PayloadAction<Partial<UserProfileSession>>) {
      if (state.session) {
        const updatedSession = {
          ...state.session,
          ...action.payload
        };
        state.session = updatedSession;
        state.lastSyncAt = Date.now();

        // Persist updated session to localStorage
        try {
          setStorageItem(STORAGE_KEYS.USER_PROFILE_SESSION, updatedSession);
        } catch (e) {
          authLogger.error('[userProfileSlice] Failed to persist session update:', e);
        }
      }
    },
    
    // SESSION MANAGEMENT: Clear on logout
    clearUserSession(state) {
      state.session = null;
      state.status = 'idle';
      state.error = null;
      state.lastSyncAt = null;
      // Clear persisted session
      removeStorageItem(STORAGE_KEYS.USER_PROFILE_SESSION);
    }
  },
});

export const {
  setUserSession,
  setProfileStatus,
  setProfileError,
  updateUserSession,
  clearUserSession
} = userProfileSlice.actions;

// SELECTORS: Convenient access to session data
export const selectUserSession = (state: { userProfile: UserProfileUiState }) => state.userProfile.session;
export const selectUserFullName = (state: { userProfile: UserProfileUiState }) => state.userProfile.session?.fullName || 'User';
export const selectUserInitials = (state: { userProfile: UserProfileUiState }) => state.userProfile.session?.initials || 'U';
export const selectUserAvatar = (state: { userProfile: UserProfileUiState }) => state.userProfile.session?.avatar;
export const selectUserRole = (state: { userProfile: UserProfileUiState }) => state.userProfile.session?.role;
export const selectIsUserOnline = (state: { userProfile: UserProfileUiState }) => state.userProfile.session?.isOnline ?? false;

export default userProfileSlice.reducer; 